import React, { useState, memo } from 'react';
import OnboardingStep from './OnboardingStep';
import CreatorOnboarding from './CreatorOnboarding';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';

const CreatorOnboardingFlow = memo(({ onComplete, onSkip }) => {
  const { isDarkMode } = useTheme();
  const { userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showChannelSetup, setShowChannelSetup] = useState(false);
  const totalSteps = 5;

  const handleNext = () => {
    if (currentStep === 2 && !userProfile?.creatorProfile?.profileComplete) {
      // Only show channel setup modal if not already set up
      setShowChannelSetup(true);
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChannelSetupComplete = () => {
    setShowChannelSetup(false);
    setCurrentStep(3); // Move to next step
  };

  if (showChannelSetup) {
    return <CreatorOnboarding onComplete={handleChannelSetupComplete} />;
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <OnboardingStep
            step={1}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
            showBack={false}
          >
            <div className="text-center">
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Welcome, Creator!
              </h2>
              <p className={`text-lg md:text-xl mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                Compete with other creators for daily promotion to our entire player base
              </p>
              <div className={`${
                isDarkMode ? 'bg-white/10' : 'bg-blue-50'
              } rounded-xl p-6 text-left`}>
                <h3 className={`text-xl font-bold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  How TheSubGames works for creators:
                </h3>
                <ul className={`space-y-2 ${
                  isDarkMode ? 'text-white/70' : 'text-gray-700'
                }`}>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🎯</span>
                    <span>Players choose you as their creator and play games</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">📈</span>
                    <span>Their game scores earn you points on the leaderboard</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🏆</span>
                    <span>Win daily at 6pm CST to get promoted to ALL players</span>
                  </li>
                </ul>
              </div>
            </div>
          </OnboardingStep>
        );

      case 2:
        return (
          <OnboardingStep
            step={2}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🔗</div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {userProfile?.creatorProfile?.profileComplete ? 'Your Channel is Connected!' : 'Connect Your Channel'}
              </h2>
              {userProfile?.creatorProfile?.profileComplete ? (
                <div>
                  <p className={`text-lg md:text-xl mb-6 ${
                    isDarkMode ? 'text-white/80' : 'text-gray-700'
                  }`}>
                    Great! Your {userProfile.primaryPlatform} channel is already connected.
                  </p>
                  <div className={`${
                    isDarkMode ? 'bg-white/10' : 'bg-blue-50'
                  } rounded-xl p-6`}>
                    <p className={`text-lg ${
                      isDarkMode ? 'text-white/70' : 'text-gray-700'
                    }`}>
                      <strong>Channel:</strong> {userProfile.displayName}
                    </p>
                  </div>
                </div>
              ) : (
                <p className={`text-lg md:text-xl mb-6 ${
                  isDarkMode ? 'text-white/80' : 'text-gray-700'
                }`}>
                  Connect your YouTube, Twitch, or TikTok channel to get started competing!
                </p>
              )}
            </div>
          </OnboardingStep>
        );

      case 3:
        return (
          <OnboardingStep
            step={3}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
          >
            <div>
              <div className="text-center mb-6">
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  You and Your Community Earn You Points
                </h2>
                <p className={`text-base ${
                  isDarkMode ? 'text-white/70' : 'text-gray-600'
                }`}>
                  The more games played, the higher your score
                </p>
              </div>
              
              {/* Visual Example */}
              <div className={`${
                isDarkMode ? 'bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-white/10' : 'bg-gradient-to-br from-blue-50 to-purple-50 border-gray-200'
              } rounded-xl p-6 border-2 mb-4 text-center`}>
                <div className="flex justify-center items-center gap-4 mb-4">
                  <div className="flex -space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold border-2 border-white">
                      P
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center text-white font-bold border-2 border-white">
                      A
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white font-bold border-2 border-white">
                      M
                    </div>
                  </div>
                  <div className="text-3xl">→</div>
                  <div className={`px-6 py-3 rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-white'}`}>
                    <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                      Your Points
                    </div>
                    <div className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                      +285
                    </div>
                  </div>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                  Your supporters play games throughout the day
                </p>
              </div>
            </div>
          </OnboardingStep>
        );

      case 4:
        return (
          <OnboardingStep
            step={4}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
          >
            <div>
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">📊</div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Watch the Leaderboard
                </h2>
                <p className={`text-base ${
                  isDarkMode ? 'text-white/70' : 'text-gray-600'
                }`}>
                  Track your rank in real-time
                </p>
              </div>
              
              {/* Mock Countdown Timer */}
              <div className={`${
                isDarkMode ? 'bg-gradient-to-r from-blue-600/20 to-pink-600/20 border-blue-400/30' : 'bg-gradient-to-r from-blue-100 to-pink-100 border-blue-300'
              } rounded-xl p-3 border-2 mb-4 text-center`}>
                <div className={`text-xs mb-1 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                  Next Winner In:
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-pink-500 bg-clip-text text-transparent">
                  05:23:17
                </div>
              </div>
              
              {/* Mock Leaderboard */}
              <div className={`${
                isDarkMode ? 'bg-white/5' : 'bg-gray-50'
              } rounded-xl p-4 space-y-2`}>
                {/* 1st Place - You */}
                <div className={`${
                  isDarkMode ? 'bg-gradient-to-r from-yellow-600/30 to-orange-600/30 border-yellow-500/50' : 'bg-gradient-to-r from-yellow-100 to-orange-100 border-yellow-400'
                } border-2 rounded-lg p-3`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">👑</span>
                      <div>
                        <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          You
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                          2 supporters
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
                        342
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                        points
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* 2nd Place */}
                <div className={`${
                  isDarkMode ? 'bg-white/5' : 'bg-white'
                } rounded-lg p-3`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">🥈</span>
                      <div>
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          OtherCreator
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                          5 supporters
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        287
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                        points
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* 3rd Place */}
                <div className={`${
                  isDarkMode ? 'bg-white/5' : 'bg-white'
                } rounded-lg p-3`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">🥉</span>
                      <div>
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          AnotherOne
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                          3 supporters
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        201
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                        points
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`${
                isDarkMode ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-300'
              } border-2 rounded-lg p-3 mt-4 text-center`}>
                <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                  <strong>💡 Tip:</strong> Be #1 when time runs out to win!
                </p>
              </div>
            </div>
          </OnboardingStep>
        );

      case 5:
        return (
          <OnboardingStep
            step={5}
            totalSteps={totalSteps}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={onSkip}
            nextLabel="Start Competing!"
            canSkip={false}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🚀</div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                You're All Set!
              </h2>
              <p className={`text-lg mb-6 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`}>
                Time to grow your audience on TheSubGames
              </p>
              
              <div className={`${
                isDarkMode ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-400/40' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-300'
              } border-2 rounded-xl p-6 space-y-4`}>
                <h3 className={`text-xl font-bold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Next steps:
                </h3>
                
                <div className="text-left space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">1</div>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      Share your profile link with your audience
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">2</div>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      Watch your supporter count grow
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">3</div>
                    <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      Compete for #1 to win daily promotion!
                    </span>
                  </div>
                </div>
                
                <div className={`${
                  isDarkMode ? 'bg-white/10' : 'bg-white'
                } rounded-lg p-4 mt-4`}>
                  <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-700'}`}>
                    🎁 <strong>Winners get:</strong> Their promotional link shown to ALL players for 24 hours
                  </p>
                </div>
              </div>
            </div>
          </OnboardingStep>
        );

      default:
        return null;
    }
  };

  return renderStepContent();
});

export default CreatorOnboardingFlow;
